import React, { useRef, useEffect, useState } from "react";
import { modifyProductionData } from "./components/easy-setup/shadow-setup/shadowCalculation";
// MUI Components
import { BottomNavigation, BottomNavigationAction } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

// Assets & Styles
import "./App.css";
import logo from "./logo.svg";

// Components & Utilities
import questions from "./components/screens/screen";

import { LogflareService } from "./crashLogging/LogFlare";
import EasySetupScreen from "./components/easy-setup/EasySetupScreen";
import EasyDashboard from "./components/easy-dashboard/EasyDashboardScreen";
import EnergyCardsContainer from "./components/easy-recommendations/RecommendationsScreen";
import Paper from "@mui/material/Paper";
import { SolarSetupDetails } from "./recommendation-calculations/SolarOptimizer";
import {
  SolarOptimizer,
  doRecommendations,
} from "./recommendation-calculations/SolarOptimizer";

const dataLoadScreenIndex = 0;
function sumArray(arr) {
  if (!Array.isArray(arr)) {
    throw new TypeError("Input must be an array");
  }
  return arr.reduce((sum, num) => sum + num, 0);
}

function App() {
  useEffect(() => {
    // Log when the component (and effectively the app) is loaded
    LogflareService.sendDataToLogflare("ACTION - PAGE_LOADED - NEW USER", 0);
  }, []);
  //GOOGLE CODE
  const handleGoogleAdsClick = () => {
    // Call the function defined in index.html
    if (window.gtag_report_conversion) {
      window.gtag_report_conversion();
    } else {
      console.error("showAlert function is not defined");
    }
  };
  //GOOGLE CODE

  function calculateMonthlyAverageSums(data) {
    // Default result in case of an error
    const defaultResult = Array(12).fill(0);

    try {
      if (data.length !== 365 * 12) {
        return defaultResult;
      }

      // Number of days in each month (non-leap year)
      const daysInMonth = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
      let monthlySums = Array(12).fill(0); // To store the total sum for each month
      let currentDayIndex = 0;

      // Loop through each month
      for (let month = 0; month < 12; month++) {
        const days = daysInMonth[month];

        for (let day = 0; day < days; day++) {
          let dailySum = 0;

          // Sum the 12 values for the current day
          for (let hour = 0; hour < 12; hour++) {
            dailySum += data[currentDayIndex * 12 + hour];
          }

          // Add the daily sum to the current month's total
          monthlySums[month] += dailySum;
          currentDayIndex++;
        }
      }

      // Calculate the average sum for each month
      return monthlySums.map((sum, month) => sum / daysInMonth[month]);
    } catch (error) {
      console.error("Error calculating monthly averages:", error.message);

      return defaultResult;
    }
  }
  function calculateDailyStatistics(data) {
    // Default result in case of an error
    const defaultResult = {
      averageDailyProduction: 0,
      maxDailyProduction: 0,
      minDailyProduction: 0,
    };

    try {
      if (data.length !== 365 * 12) {
        return defaultResult;
      }

      const dailyProductions = [];

      // Loop through each day and calculate the daily sum
      for (let day = 0; day < 365; day++) {
        let dailySum = 0;

        // Sum the 12 hourly values for the current day
        for (let hour = 0; hour < 12; hour++) {
          dailySum += data[day * 12 + hour];
        }

        dailyProductions.push(dailySum);
      }

      // Calculate statistics
      const totalProduction = dailyProductions.reduce(
        (acc, value) => acc + value,
        0
      );
      const averageDailyProduction = totalProduction / 365;
      const maxDailyProduction = Math.max(...dailyProductions);
      const minDailyProduction = Math.min(...dailyProductions);

      // Return results
      return {
        averageDailyProduction,
        maxDailyProduction,
        minDailyProduction,
      };
    } catch (error) {
      console.error("Error calculating daily statistics:", error.message);

      return defaultResult;
    }
  }

  const [recommendations, setRecommendations] = React.useState([]);
  const findClosestValue = (value, options) => {
    return options.reduce((prev, curr) =>
      Math.abs(curr - value) < Math.abs(prev - value) ? curr : prev
    );
  };
  const loadProductionData = async (orientation, tilt) => {
    // Define the available orientations and tilt angles
    const orientations = [-90, -45, 0, 45, 90];
    const tilts = [0, 30, 60, 90];

    // Find the closest values
    const closestOrientation = findClosestValue(orientation, orientations);
    const closestTilt = findClosestValue(tilt, tilts);

    // Location and shadow status are constants
    const location = "Rajasthan";
    const shadowStatus = "noshadow";

    // Construct the filename
    const filename = `data_location_${location}_orientation_${closestOrientation}_tilt_${closestTilt}_${shadowStatus}.json`;

    try {
      const response = await fetch(`/data/${filename}`);
      if (!response.ok) {
        throw new Error(`File not found: ${filename}`);
      }
      return await response.json();
    } catch (error) {
      console.error(`Error loading production data: ${error.message}`);
      return null; // Handle fallback logic here if needed
    }
  };
  const handleOrientationChange = async (value) => {
    handleFieldChange("orientation", value);
  };

  const handleTiltAngleChange = async (value) => {
    handleFieldChange("tiltAngle", value);
  };

  const [currentScreenIndex, setCurrentScreenIndex] = useState(0);

  // Screen Rendering

  const handleHorizonChange = (newValue) => {
    handleFieldChange("shadowSettings", newValue);
  };

  const [solarConfig, setSolarConfig] = useState({
    orientation: 0, // Float for slider
    tiltAngle: 15, // String or number for dropdown
    peakPower: 3.0, // Float for slider
    hourlyProduction: [],
    productionData: [],
    systemType: -1,
    consumption: [
      [0.9, 0.8, 0.7, 0.6, 0.7, 0.9, 1.1, 1.3, 1.4, 1.3, 1.1, 0.9],
      [0.6, 0.5, 0.5, 0.4, 0.6, 0.7, 0.9, 1.0, 1.1, 1.0, 0.9, 0.7],
      [0.7, 0.7, 0.6, 0.5, 0.6, 0.8, 1.0, 1.1, 1.3, 1.2, 1.1, 0.9],
      [0.8, 0.7, 0.7, 0.6, 0.7, 0.9, 1.2, 1.3, 1.4, 1.3, 1.1, 0.9],
    ],
    budget: 100.0,
    shadowSettings: [],
    // Add more fields as needed
  });
  const [fieldsTouched, setFieldsTouched] = useState({
    orientation: false,
    tiltAngle: false,
    peakPower: false,
    shadowSettings: false,
    systemType: false,
    consumption: false,
    budget: false,
  });
  const handleFieldChange = (field, value) => {
    updateConfig(field, value); // Update solarConfig
    setFieldsTouched((prev) => ({
      ...prev,
      [field]: true,
    }));
  };

  const [chosenSetup, setChosenSetup] = useState(
    new SolarSetupDetails(
      "Lower cost option",
      1370,
      472,
      0.4,
      1.2,
      0,
      1.2 * 1000
    )
  );

  // Handler to update the state dynamically
  const updateConfig = (key, value) => {
    setSolarConfig((prevConfig) => ({
      ...prevConfig,
      [key]: value,
    }));

    console.log("EZ VAN NALUNK: ");
    console.log(solarConfig);
  };
  const areAllFieldsTouched = () => {
    return Object.values(fieldsTouched).every((touched) => touched);
  };

  // Function to update the screen index
  const handleNavigationChange = async (event, newValue) => {
    //CALCULATION PART
    if (currentScreenIndex == dataLoadScreenIndex) {
      if (areAllFieldsTouched()) {
        const productionData = await loadProductionData(
          solarConfig.orientation,
          solarConfig.tiltAngle
        );
        let productionArray = productionData[0].production_hourly;
        console.log("PRODUCTION SUM BEFORE:", sumArray(productionArray) / 1000);
        const shadowedData = modifyProductionData(
          productionArray,
          solarConfig.shadowSettings
        );

        console.log("PRODUCTION SUM AFTER:", sumArray(shadowedData) / 1000);
        const scaledShadowedData = shadowedData.map((value) => value / 1000);
        updateConfig("productionData", scaledShadowedData);
        const recommendations = doRecommendations(
          solarConfig.systemType,
          solarConfig.consumption,
          scaledShadowedData,
          solarConfig.budget,
          solarConfig.peakPower
        );
        console.log("EZT AJANLOM NEKED FASZFEJ");
        console.log(recommendations);
        setRecommendations(recommendations);
        setChosenSetup(recommendations[0]);
      } else {
        const untouchedFields = Object.keys(fieldsTouched).filter(
          (key) => !fieldsTouched[key]
        );
        alert(
          `Please complete the following fields: ${untouchedFields.join(", ")}`
        );

        return; // Prevent navigation
      }
    }
    if (newValue === 0 && currentScreenIndex > 0) {
      LogflareService.sendDataToLogflare(
        "ACTION - NAVIGATING BACKWARD FROM:",
        currentScreenIndex
      );
      setCurrentScreenIndex(currentScreenIndex - 1); // Navigate to the previous screen
    } else if (newValue === 1 && currentScreenIndex < 2) {
      LogflareService.sendDataToLogflare(
        "ACTION - NAVIGATING FORWARD FROM:",
        currentScreenIndex
      );
      setCurrentScreenIndex(currentScreenIndex + 1); // Navigate to the next screen
    }
  };
  const findOutMoreClicked = (recommendation) => {
    handleGoogleAdsClick();
    setChosenSetup(recommendation);
    setCurrentScreenIndex(currentScreenIndex + 1);

    LogflareService.sendDataToLogflare("ACTION - FIND OUT MORE CLICKED:", 1);
    // Perform navigation or other logic
  };
  const stats = calculateDailyStatistics(solarConfig.productionData);
  // Screen components mapped to indices
  const screenComponents = [
    <EasySetupScreen
      orientation={solarConfig.orientation}
      onOrientationChange={handleOrientationChange} // Updated handler
      tiltAngle={solarConfig.tiltAngle}
      onTiltAngleChange={handleTiltAngleChange} // Updated handler
      peakPower={solarConfig.peakPower}
      onPeakPowerChange={(value) => handleFieldChange("peakPower", value)}
      productionData={[]}
      onShadowChange={handleHorizonChange}
      systemType={solarConfig.systemType}
      onSystemTypeChange={(value) => handleFieldChange("systemType", value)}
      onConsumptionChange={(value) => handleFieldChange("consumption", value)}
      budget={solarConfig.budget}
      onBudgetChange={(value) => handleFieldChange("budget", value)}
    />,
    <EnergyCardsContainer
      recommendations={recommendations}
      onFindOutMore={findOutMoreClicked}
    />,
    <EasyDashboard
      hourlyProduction={calculateMonthlyAverageSums(solarConfig.productionData)}
      chosenSetupDetail={chosenSetup}
      avgDailyProduction={stats.averageDailyProduction}
      maxDailyProduction={stats.maxDailyProduction}
      minDailyProduction={stats.minDailyProduction}
    />,
  ];

  return (
    <div className="App">
      {screenComponents[currentScreenIndex]}

      <Paper
        sx={{ position: "fixed", bottom: 0, left: 0, right: 0 }}
        elevation={3}
      >
        <BottomNavigation
          showLabels
          value={currentScreenIndex} // No highlighting needed for "Previous" or "Next"
          onChange={handleNavigationChange}
        >
          <BottomNavigationAction label="Previous" icon={<ArrowBackIcon />} />
          <BottomNavigationAction label="Next" icon={<ArrowForwardIcon />} />
        </BottomNavigation>
      </Paper>
    </div>
  );
}

export default App;
